import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Trash2, ChevronDown, ChevronRight, Mail, Phone,
  MapPin, User, Users, Calendar, Search,
} from "lucide-react";

const STATUSES = [
  { key: "new",       label: "New",       color: "bg-[color:var(--honey)] text-[color:var(--blue-ink)]" },
  { key: "contacted", label: "Contacted", color: "bg-blue-100 text-blue-800" },
  { key: "approved",  label: "Approved",  color: "bg-emerald-100 text-emerald-800" },
  { key: "rejected",  label: "Rejected",  color: "bg-red-100 text-red-700" },
];

function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
      {s.label}
    </span>
  );
}

export default function AdminMembership() {
  const { authedClient } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/membership/applications");
      setApps(data);
    } catch {
      toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await authedClient().patch(`/admin/membership/applications/${id}`, { status });
      toast.success(`Marked ${status}`);
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently? This cannot be undone.")) return;
    try {
      await authedClient().delete(`/admin/membership/applications/${id}`);
      toast.success("Deleted");
      setApps((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = useMemo(() => {
    const c = { all: apps.length, new: 0, contacted: 0, approved: 0, rejected: 0 };
    for (const a of apps) c[a.status || "new"] = (c[a.status || "new"] || 0) + 1;
    return c;
  }, [apps]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return apps.filter((a) => {
      if (filter !== "all" && (a.status || "new") !== filter) return false;
      if (!needle) return true;
      const hay = [
        a.household_name, a.primary_email, a.primary_phone, a.city, a.state,
        ...(a.adults || []).map((x) => `${x.name} ${x.email || ""}`),
      ].join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [apps, filter, q]);

  return (
    <div data-testid="admin-membership-page">
      <div className="mb-6">
        <p className="eyebrow mb-2">★ ADMIN</p>
        <h1 className="font-display text-3xl text-[color:var(--blue-ink)]">Membership applications</h1>
        <p className="text-sm text-[color:var(--ink-soft)] mt-1">
          All applications submitted through /membership. Update status as you contact families.
        </p>
      </div>

      {/* Filters + search */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1.5 rounded-full bg-[color:var(--cream)] p-1">
          {[["all","All"], ["new","New"], ["contacted","Contacted"], ["approved","Approved"], ["rejected","Rejected"]].map(([k, l]) => (
            <button key={k}
              onClick={() => setFilter(k)}
              data-testid={`filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-sm ${filter === k
                ? "bg-white text-[color:var(--blue-deep)] shadow-sm font-semibold"
                : "text-[color:var(--ink-soft)] hover:text-[color:var(--blue-deep)]"
              }`}>
              {l} <span className="ml-1 text-xs opacity-70">{counts[k] || 0}</span>
            </button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--muted)]" />
          <input type="search"
            placeholder="Search name, email, phone..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 pr-3 py-2 rounded-full border border-[color:var(--line)] text-sm min-w-[240px] bg-white"
            data-testid="admin-membership-search" />
        </div>
      </div>

      {loading && <p className="text-center py-12 text-[color:var(--muted)]">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <p className="text-center py-16 text-[color:var(--muted)]">
          {apps.length === 0
            ? "No applications yet. New submissions from /membership appear here."
            : "No applications match this filter."}
        </p>
      )}

      <div className="space-y-3">
        {filtered.map((app) => {
          const isOpen = expanded === app.id;
          const date = app.submitted_at
            ? new Date(app.submitted_at).toLocaleString(undefined, {
                dateStyle: "medium", timeStyle: "short",
              })
            : "";
          return (
            <article key={app.id}
              className="rounded-xl border border-[color:var(--line)] bg-white overflow-hidden"
              data-testid={`app-row-${app.id}`}>
              <header
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[color:var(--cream)]/60"
                onClick={() => setExpanded(isOpen ? null : app.id)}>
                <button className="text-[color:var(--muted)]" aria-label={isOpen ? "Collapse" : "Expand"}>
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-[color:var(--blue-ink)] truncate">{app.household_name}</span>
                    <StatusBadge status={app.status || "new"} />
                    {app.membership_type && (
                      <span className="text-xs text-[color:var(--ink-soft)] bg-[color:var(--cream)] px-2 py-0.5 rounded-full">
                        {app.membership_type}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--ink-soft)] mt-1 flex flex-wrap gap-x-3">
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {app.primary_email}</span>
                    {app.primary_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {app.primary_phone}</span>}
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {date}</span>
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {(app.adults||[]).length} adults · {(app.children||[]).length} kids</span>
                  </div>
                </div>
              </header>

              {isOpen && (
                <div className="border-t border-[color:var(--line)] p-5 bg-[color:var(--cream)]/40 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Section title="Contact">
                      <Line icon={Mail}  label="Email" val={<a href={`mailto:${app.primary_email}`} className="text-[color:var(--blue-deep)] hover:underline">{app.primary_email}</a>} />
                      <Line icon={Phone} label="Phone" val={app.primary_phone || "-"} />
                      <Line icon={MapPin} label="Address" val={[app.address, app.city, app.state, app.zip_code].filter(Boolean).join(", ") || "-"} />
                    </Section>
                    <Section title={`Adults (${(app.adults||[]).length})`}>
                      {(app.adults||[]).length === 0
                        ? <p className="text-xs text-[color:var(--muted)]">No adults listed.</p>
                        : (app.adults||[]).map((a, i) => {
                            const meta = [
                              a.email,
                              a.cell_phone && `Cell: ${a.cell_phone}`,
                              a.home_phone && `Home: ${a.home_phone}`,
                              a.phone,
                              a.birthday && `DOB: ${a.birthday}`,
                              a.anniversary && `Anniversary: ${a.anniversary}`,
                              a.occupation,
                              a.marital_status,
                              a.religious_tradition,
                              a.religious_status,
                              a.hebrew_name && `Hebrew: ${a.hebrew_name}`,
                              a.directory_ok === false && "Not in directory",
                            ].filter(Boolean).join(" · ");
                            return (
                              <div key={i} className="text-sm">
                                <div className="font-medium text-[color:var(--blue-ink)] flex items-center gap-1.5"><User className="w-3 h-3" /> {a.name}</div>
                                <div className="text-xs text-[color:var(--ink-soft)] ml-4">
                                  {meta || "-"}
                                </div>
                              </div>
                            );
                          })}
                    </Section>
                  </div>
                  {(app.children||[]).length > 0 && (
                    <Section title={`Children (${(app.children||[]).length})`}>
                      {(app.children||[]).map((c, i) => (
                        <div key={i} className="text-sm">
                          <div className="font-medium text-[color:var(--blue-ink)]">{c.name}</div>
                          <div className="text-xs text-[color:var(--ink-soft)] ml-2">
                            {[c.age && `Age ${c.age}`, c.birthday && `DOB: ${c.birthday}`, c.hebrew_name && `Hebrew: ${c.hebrew_name}`].filter(Boolean).join(" · ") || "-"}
                          </div>
                        </div>
                      ))}
                    </Section>
                  )}
                  {(app.yahrzeits||[]).length > 0 && (
                    <Section title={`Yahrzeit remembrances (${(app.yahrzeits||[]).length})`}>
                      {(app.yahrzeits||[]).map((y, i) => (
                        <div key={i} className="text-sm">
                          <div className="font-medium text-[color:var(--blue-ink)]">
                            {[y.first_name, y.last_name].filter(Boolean).join(" ") || "(unnamed)"}
                          </div>
                          <div className="text-xs text-[color:var(--ink-soft)] ml-2">
                            {[
                              y.hebrew_name && `Hebrew: ${y.hebrew_name}`,
                              y.related_to && `For: ${y.related_to}`,
                              y.relationship,
                              y.date_of_death && `Date: ${y.date_of_death}`,
                            ].filter(Boolean).join(" · ") || "-"}
                          </div>
                        </div>
                      ))}
                    </Section>
                  )}
                  {((app.committees||[]).length > 0 || (app.contact_prefs||[]).length > 0 || app.photo_release_opt_out || app.typed_signature) && (
                    <div className="grid md:grid-cols-2 gap-4">
                      {(app.committees||[]).length > 0 && (
                        <Section title="Interested committees">
                          <div className="flex flex-wrap gap-1.5">
                            {(app.committees||[]).map((c) => (
                              <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-white border border-[color:var(--line)] text-[color:var(--blue-ink)]">{c}</span>
                            ))}
                          </div>
                        </Section>
                      )}
                      {(app.contact_prefs||[]).length > 0 && (
                        <Section title="Preferred contact">
                          <p className="text-sm text-[color:var(--ink)]">{(app.contact_prefs||[]).join(", ")}</p>
                        </Section>
                      )}
                      {(app.photo_release_opt_out || app.typed_signature) && (
                        <Section title="Consent & signature">
                          {app.photo_release_opt_out && (
                            <p className="text-sm text-red-700">Opted OUT of photo release</p>
                          )}
                          {app.typed_signature && (
                            <p className="text-sm text-[color:var(--ink)]">Signed: <strong>{app.typed_signature}</strong></p>
                          )}
                        </Section>
                      )}
                    </div>
                  )}
                  {(app.interests || app.heard_from || app.questions) && (
                    <div className="space-y-3">
                      {app.interests && <Callout title="What they're looking for">{app.interests}</Callout>}
                      {app.heard_from && <Callout title="How they heard about us">{app.heard_from}</Callout>}
                      {app.questions && <Callout title="Questions & notes">{app.questions}</Callout>}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-[color:var(--line)]">
                    <span className="text-xs text-[color:var(--ink-soft)] mr-2 self-center">Change status:</span>
                    {STATUSES.map((s) => (
                      <button key={s.key}
                        onClick={() => setStatus(app.id, s.key)}
                        disabled={(app.status || "new") === s.key}
                        data-testid={`set-${s.key}-${app.id}`}
                        className={`text-xs px-3 py-1.5 rounded-full font-medium transition-opacity ${s.color} ${(app.status || "new") === s.key ? "opacity-50 cursor-default" : "hover:opacity-80"}`}>
                        {s.label}
                      </button>
                    ))}
                    <a href={`mailto:${app.primary_email}?subject=Re: Membership at Temple Israel of Brevard`}
                      className="text-xs px-3 py-1.5 rounded-full font-medium bg-white border border-[color:var(--line)] text-[color:var(--blue-deep)] hover:bg-[color:var(--cream)]">
                      <Mail className="inline w-3 h-3 mr-1" /> Reply
                    </a>
                    <button onClick={() => remove(app.id)}
                      data-testid={`delete-${app.id}`}
                      className="text-xs px-3 py-1.5 rounded-full font-medium text-red-600 border border-red-200 hover:bg-red-50 ml-auto">
                      <Trash2 className="inline w-3 h-3 mr-1" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wider text-[color:var(--muted)] font-semibold mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Line({ icon: Icon, label, val }) {
  return (
    <div className="text-sm">
      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[color:var(--muted)] mr-2">
        <Icon className="w-3 h-3" /> {label}
      </span>
      <span className="text-[color:var(--ink)]">{val}</span>
    </div>
  );
}

function Callout({ title, children }) {
  return (
    <div className="rounded-lg bg-white border-l-4 border-[color:var(--honey)] px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] font-semibold mb-1">{title}</div>
      <div className="text-sm text-[color:var(--ink)] whitespace-pre-wrap">{children}</div>
    </div>
  );
}
